import { useRef, useState } from "react";
import { message } from "antd";
import type { Rule } from "antd/es/form";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { POKER_CONFIGURATION_PAGES } from "../../../../utils/constants/poker/pokerConfigurationData";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import { formatDate } from "../../../../utils/dateService";
import {
  CashGameConfigSaveError,
  useCashGameConfig,
  useUpdateCashGameConfig,
  type CashGameConfig,
  type CashGameConfigKey,
  type CashGameCurrency,
  type CashGameVisibility,
} from "../../../../queries/poker/useCashGameConfig";
import {
  Button,
  Form,
  Input,
  RebetLoader,
  SegmentedControl,
  ToggleSwitch,
} from "../../../shared";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import { PokerConfigurationShell } from "../PokerConfigurationShell";
import { PokerConfigurationPlaceholder } from "../PokerConfigurationPlaceholder";

import styles from "./CashGameLimits.module.css";

const PAGE = POKER_CONFIGURATION_PAGES.cashGameLimits;

/** Poker-standard bounds. Big blind is conventionally 2x the small blind
 *  (3x covers 1/3-style structures); cash-game buy-ins run 20–250 big
 *  blinds across the industry. The numbers are baked into the matching
 *  POKER_CASH_GAME_* rule strings — change both together. */
const BIG_BLIND_MIN_RATIO = 2;
const BIG_BLIND_MAX_RATIO = 3;
const BUY_IN_BB_FLOOR = 20;
const BUY_IN_BB_CEILING = 250;
/** Float-noise tolerance for ratio comparisons (0.1 * 3 !== 0.3). */
const RATIO_EPSILON = 1e-9;

/** Real-world blind denominations — the steppers walk this ladder instead
 *  of adding a fixed amount, the way a bet-size stepper does. */
const BLIND_LADDER = [
  0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 25, 50, 100,
] as const;

/** Prefilled when no config has been loaded yet: the standard table from
 *  the POKENG-142 contract sample (6-max, 0.25/0.50, 40–100bb, 5% rake
 *  capped at 4, no-flop-no-drop on). */
const CONFIG_DEFAULTS: CashGameConfig = {
  maxPlayers: 6,
  minPlayers: 2,
  smallBlind: 0.25,
  bigBlind: 0.5,
  buyInBbMin: 40,
  buyInBbMax: 100,
  gameStartDelay: 5,
  handStartDelay: 15,
  rakePercentage: 5,
  rakeCap: 4,
  noFlopNoDrop: true,
  allowNickname: false,
  isAvEnabled: false,
};

const CURRENCY_ITEMS = [
  { id: "cash" as CashGameCurrency, label: AppStrings.POKER_CASH_GAME_CURRENCY_CASH },
  { id: "coin" as CashGameCurrency, label: AppStrings.POKER_CASH_GAME_CURRENCY_COIN },
];

const VISIBILITY_ITEMS = [
  {
    id: "public" as CashGameVisibility,
    label: AppStrings.POKER_CASH_GAME_VISIBILITY_PUBLIC,
  },
  {
    id: "private" as CashGameVisibility,
    label: AppStrings.POKER_CASH_GAME_VISIBILITY_PRIVATE,
  },
];

/** Form model — numeric inputs are typeable text fields, so values live as
 *  strings and are parsed on validate/save; toggles are booleans.
 *  `allow_nickname` / `is_av_enabled` exist in the API config but have no
 *  inputs on this page — they're passed through unchanged on save. */
type CashGameFormValues = {
  maxPlayers: string;
  minPlayers: string;
  smallBlind: string;
  bigBlind: string;
  buyInBbMin: string;
  buyInBbMax: string;
  gameStartDelay: string;
  handStartDelay: string;
  rakePercentage: string;
  rakeCap: string;
  noFlopNoDrop: boolean;
};

/** Config fields the page doesn't edit but must echo back in the full PUT. */
type PassthroughFields = Pick<CashGameConfig, "allowNickname" | "isAvEnabled">;

const TEXT_FIELD_NAMES = [
  "maxPlayers",
  "minPlayers",
  "smallBlind",
  "bigBlind",
  "buyInBbMin",
  "buyInBbMax",
  "gameStartDelay",
  "handStartDelay",
  "rakePercentage",
  "rakeCap",
] as const;

const TOGGLE_FIELD_NAMES = ["noFlopNoDrop"] as const;

const FIELD_NAMES: Array<keyof CashGameFormValues> = [
  ...TEXT_FIELD_NAMES,
  ...TOGGLE_FIELD_NAMES,
];

function toFormValues(config: CashGameConfig): CashGameFormValues {
  const asString = (value: number | null) =>
    value === null ? "" : String(value);
  return {
    maxPlayers: asString(config.maxPlayers),
    minPlayers: asString(config.minPlayers),
    smallBlind: asString(config.smallBlind),
    bigBlind: asString(config.bigBlind),
    buyInBbMin: asString(config.buyInBbMin),
    buyInBbMax: asString(config.buyInBbMax),
    gameStartDelay: asString(config.gameStartDelay),
    handStartDelay: asString(config.handStartDelay),
    rakePercentage: asString(config.rakePercentage),
    rakeCap: asString(config.rakeCap),
    noFlopNoDrop: config.noFlopNoDrop,
  };
}

function toConfig(
  values: CashGameFormValues,
  passthrough: PassthroughFields,
): CashGameConfig {
  return {
    maxPlayers: Number(values.maxPlayers),
    minPlayers: Number(values.minPlayers),
    smallBlind: Number(values.smallBlind),
    bigBlind: Number(values.bigBlind),
    buyInBbMin: Number(values.buyInBbMin),
    buyInBbMax: Number(values.buyInBbMax),
    gameStartDelay: Number(values.gameStartDelay),
    handStartDelay: Number(values.handStartDelay),
    rakePercentage: Number(values.rakePercentage),
    rakeCap: Number(values.rakeCap),
    noFlopNoDrop: !!values.noFlopNoDrop,
    allowNickname: passthrough.allowNickname,
    isAvEnabled: passthrough.isAvEnabled,
  };
}

const asNumber = (raw: unknown): number | null => {
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
};

/** Numeric-aware text comparison so typing "5.0" over a loaded "5" doesn't
 *  read as an edit (no dirty chip, no no-op PUT). Non-numeric text falls
 *  back to trimmed string equality. */
function sameText(a: string | undefined, b: string | undefined): boolean {
  const left = (a ?? "").trim();
  const right = (b ?? "").trim();
  if (left === right) return true;
  const leftNumber = asNumber(left);
  const rightNumber = asNumber(right);
  return leftNumber !== null && rightNumber !== null && leftNumber === rightNumber;
}

function sameValues(a: CashGameFormValues, b: CashGameFormValues): boolean {
  return (
    TEXT_FIELD_NAMES.every((field) => sameText(a[field], b[field])) &&
    TOGGLE_FIELD_NAMES.every((field) => !!a[field] === !!b[field])
  );
}

/** Number rule builder. Empty values are skipped so the `required` rule owns
 *  that case; `min` is the lowest allowed value (0 for the delay fields);
 *  `max` carries its own message so the text stays field-appropriate. */
const numberRule = ({
  integer,
  min = "positive",
  max,
}: {
  integer: boolean;
  min?: "positive" | "zero";
  max?: { value: number; message: string };
}): Rule => ({
  validator(_, raw: unknown) {
    if (raw === undefined || raw === null || raw === "") {
      return Promise.resolve();
    }
    const n = asNumber(raw);
    if (n === null) {
      return Promise.reject(new Error(AppStrings.POKER_CASH_GAME_NUMBER));
    }
    if (min === "positive" && n <= 0) {
      return Promise.reject(new Error(AppStrings.POKER_CASH_GAME_POSITIVE));
    }
    if (min === "zero" && n < 0) {
      return Promise.reject(
        new Error(AppStrings.POKER_CASH_GAME_NON_NEGATIVE),
      );
    }
    if (max !== undefined && n > max.value) {
      return Promise.reject(new Error(max.message));
    }
    if (integer && !Number.isInteger(n)) {
      return Promise.reject(new Error(AppStrings.POKER_CASH_GAME_INTEGER));
    }
    return Promise.resolve();
  },
});

/** Money values are 2-dp decimals (matches the BE Decimal convention). */
const twoDecimalsRule: Rule = {
  validator(_, raw: unknown) {
    const decimals = String(raw ?? "").split(".")[1];
    if (decimals && decimals.length > 2) {
      return Promise.reject(
        new Error(AppStrings.POKER_CASH_GAME_TWO_DECIMALS),
      );
    }
    return Promise.resolve();
  },
};

/** Poker structure sanity: the big blind sits at 2x–3x the small blind
 *  (2x is the norm; 3x covers 1/3-style games). The 2x floor also covers
 *  the ticket AC that big ≤ small is blocked — one rule, one message. */
const bigBlindRatioRule: Rule = ({ getFieldValue }) => ({
  validator(_, raw: unknown) {
    const big = asNumber(raw);
    const small = asNumber(getFieldValue("smallBlind"));
    if (
      big !== null &&
      small !== null &&
      small > 0 &&
      (big < small * BIG_BLIND_MIN_RATIO - RATIO_EPSILON ||
        big > small * BIG_BLIND_MAX_RATIO + RATIO_EPSILON)
    ) {
      return Promise.reject(
        new Error(AppStrings.POKER_CASH_GAME_BIG_BLIND_RATIO_RULE),
      );
    }
    return Promise.resolve();
  },
});

/** Cash-game min buy-in floor: below 20bb isn't a playable stack. */
const minBuyInFloorRule: Rule = {
  validator(_, raw: unknown) {
    const n = asNumber(raw);
    if (n !== null && n > 0 && n < BUY_IN_BB_FLOOR) {
      return Promise.reject(
        new Error(AppStrings.POKER_CASH_GAME_MIN_BUY_IN_FLOOR_RULE),
      );
    }
    return Promise.resolve();
  },
};

/** Cash-game max buy-in ceiling: 250bb is the deep-stack upper bound. */
const maxBuyInCeilingRule: Rule = {
  validator(_, raw: unknown) {
    const n = asNumber(raw);
    if (n !== null && n > BUY_IN_BB_CEILING) {
      return Promise.reject(
        new Error(AppStrings.POKER_CASH_GAME_MAX_BUY_IN_CEILING_RULE),
      );
    }
    return Promise.resolve();
  },
};

/** Min buy-in multiplier must not exceed the max. */
const minBuyInBelowMax: Rule = ({ getFieldValue }) => ({
  validator(_, raw: unknown) {
    const min = asNumber(raw);
    const max = asNumber(getFieldValue("buyInBbMax"));
    if (min !== null && max !== null && min > max) {
      return Promise.reject(new Error(AppStrings.POKER_CASH_GAME_BUY_IN_RULE));
    }
    return Promise.resolve();
  },
});

/** Min players to start must not exceed the table's seat count. */
const minPlayersBelowMax: Rule = ({ getFieldValue }) => ({
  validator(_, raw: unknown) {
    const min = asNumber(raw);
    const max = asNumber(getFieldValue("maxPlayers"));
    if (min !== null && max !== null && min > max) {
      return Promise.reject(
        new Error(AppStrings.POKER_CASH_GAME_MIN_PLAYERS_RULE),
      );
    }
    return Promise.resolve();
  },
});

const requiredRule: Rule = {
  required: true,
  message: AppStrings.POKER_CASH_GAME_REQUIRED,
};

/** Next rung on a denomination ladder (used for blinds, like a bet-size
 *  stepper): values between rungs snap to the nearest rung in the step
 *  direction; the ends clamp. */
function stepLadder(
  ladder: readonly number[],
  current: number | null,
  direction: 1 | -1,
): number {
  if (current === null) return ladder[0];
  if (direction === 1) {
    return (
      ladder.find((rung) => rung > current + RATIO_EPSILON) ??
      ladder[ladder.length - 1]
    );
  }
  return (
    [...ladder].reverse().find((rung) => rung < current - RATIO_EPSILON) ??
    ladder[0]
  );
}

type StepperInputProps = {
  /** Injected by antd Form.Item. */
  value?: string;
  onChange?: (next: string) => void;
  /** Fixed step amount; ignored when `ladder` is set. */
  step?: number;
  /** Denomination ladder to walk instead of a fixed step. */
  ladder?: readonly number[];
  /** Lowest value the stepper buttons will go to (typing is still free-form
   *  — the field rules own validation). */
  min?: number;
  hint?: string;
  inputMode?: "numeric" | "decimal";
  "data-testid"?: string;
  "aria-label"?: string;
};

/** Text input with − / + stepper buttons (the bet-size stepper pattern).
 *  Typing stays free-form; the buttons produce poker-sensible values. */
function StepperInput({
  value = "",
  onChange,
  step = 1,
  ladder,
  min = 0,
  hint,
  inputMode = "decimal",
  "data-testid": testId,
  "aria-label": ariaLabel,
}: StepperInputProps) {
  const stepTo = (direction: 1 | -1) => {
    const current = asNumber(value);
    let next: number;
    if (ladder) {
      next = stepLadder(ladder, current, direction);
    } else if (current === null) {
      next = min;
    } else {
      next = Math.round((current + direction * step) * 100) / 100;
    }
    onChange?.(String(Math.max(next, min)));
  };

  const stepButton = (direction: 1 | -1) => (
    <button
      type="button"
      className={styles.stepButton}
      data-testid={testId ? `${testId}-step-${direction === 1 ? "up" : "down"}` : undefined}
      aria-label={`${
        direction === 1
          ? AppStrings.POKER_CASH_GAME_STEP_UP
          : AppStrings.POKER_CASH_GAME_STEP_DOWN
      } ${ariaLabel ?? ""}`.trim()}
      onClick={() => stepTo(direction)}
    >
      <span
        className={direction === 1 ? styles.stepArrowUp : styles.stepArrowDown}
      />
    </button>
  );

  return (
    <Input
      value={value}
      onChange={(event) => onChange?.(event.target.value)}
      inputMode={inputMode}
      data-testid={testId}
      aria-label={ariaLabel}
      suffix={
        <span className={styles.stepSuffix}>
          {hint ? <span className={styles.inputHint}>{hint}</span> : null}
          {/* Stacked ▲/▼ control on the right edge, bet-input style. */}
          <span className={styles.stepControls}>
            {stepButton(1)}
            {stepButton(-1)}
          </span>
        </span>
      }
    />
  );
}

type ToggleFieldProps = {
  name: keyof CashGameFormValues;
  label: string;
  hint: string;
  testId: string;
};

function ToggleField({ name, label, hint, testId }: ToggleFieldProps) {
  return (
    <div className={styles.toggleRow} data-testid={testId}>
      <div>
        <p className={styles.toggleLabel}>{label}</p>
        <p className={styles.toggleHint}>{hint}</p>
      </div>
      <Form.Item name={name} noStyle>
        <ToggleSwitch tone="info" ariaLabel={label} />
      </Form.Item>
    </div>
  );
}

type CashGameLimitsFormProps = {
  configKey: CashGameConfigKey;
  /** Last-saved server config — the baseline Save/Discard diff against. */
  initialConfig: CashGameConfig;
  /** Lets the page block config switching while there are unsaved edits. */
  onDirtyChange: (dirty: boolean) => void;
};

function CashGameLimitsForm({
  configKey,
  initialConfig,
  onDirtyChange,
}: CashGameLimitsFormProps) {
  const [form] = Form.useForm<CashGameFormValues>();
  const setFieldErrors = Form.useApiFormErrors(form);
  const updateMutation = useUpdateCashGameConfig();

  const [baseline, setBaseline] = useState<CashGameFormValues>(() =>
    toFormValues(initialConfig),
  );
  // Not edited on this page, but the PUT sends the full config — echo the
  // loaded (then last-saved) values back so they're never clobbered.
  const [passthrough, setPassthrough] = useState<PassthroughFields>(() => ({
    allowNickname: initialConfig.allowNickname,
    isAvEnabled: initialConfig.isAvEnabled,
  }));
  const [isDirty, setIsDirtyState] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  // Big blind tracks 2x the small blind until the admin edits it directly.
  const bigBlindTouched = useRef(false);

  const setIsDirty = (dirty: boolean) => {
    setIsDirtyState(dirty);
    onDirtyChange(dirty);
  };

  const applyValues = (values: CashGameFormValues) => {
    form.setFieldsValue(values);
    // Programmatic sets don't re-run rules — drop any stale inline errors.
    form.setFields(FIELD_NAMES.map((name) => ({ name, errors: [] })));
    bigBlindTouched.current = false;
    setIsDirty(false);
    setHasErrors(false);
  };

  const handleValuesChange = (
    changed: Partial<CashGameFormValues>,
    all: CashGameFormValues,
  ) => {
    let next = all;
    if (changed.bigBlind !== undefined) {
      bigBlindTouched.current = true;
    } else if (changed.smallBlind !== undefined && !bigBlindTouched.current) {
      const small = asNumber(changed.smallBlind);
      if (small !== null && small > 0) {
        const derived = String(
          Math.round(small * BIG_BLIND_MIN_RATIO * 100) / 100,
        );
        form.setFieldsValue({ bigBlind: derived });
        form.validateFields(["bigBlind"]).catch(() => {});
        next = { ...all, bigBlind: derived };
      }
    }
    setIsDirty(!sameValues(next, baseline));
  };

  const handleDiscard = () => applyValues(baseline);

  const handleSave = async () => {
    let values: CashGameFormValues;
    try {
      values = await form.validateFields();
    } catch {
      // antd renders the inline messages; Save stays disabled via hasErrors.
      return;
    }
    try {
      const saved = await updateMutation.mutateAsync({
        key: configKey,
        config: toConfig(values, passthrough),
      });
      const savedValues = toFormValues(saved.config);
      setPassthrough({
        allowNickname: saved.config.allowNickname,
        isAvEnabled: saved.config.isAvEnabled,
      });
      setBaseline(savedValues);
      applyValues(savedValues);
      message.success(AppStrings.POKER_CASH_GAME_SAVE_SUCCESS);
    } catch (error) {
      if (
        error instanceof CashGameConfigSaveError &&
        error.fieldErrors.length > 0
      ) {
        setFieldErrors(error.fieldErrors);
        setHasErrors(true);
        return;
      }
      message.error(
        getApiErrorMessage(error, AppStrings.POKER_CASH_GAME_SAVE_ERROR),
      );
    }
  };

  const saveDisabled = !isDirty || hasErrors || updateMutation.isPending;

  return (
    <Form<CashGameFormValues>
      form={form}
      initialValues={baseline}
      onValuesChange={handleValuesChange}
      onFieldsChange={() =>
        setHasErrors(
          form.getFieldsError().some((field) => field.errors.length > 0),
        )
      }
    >
      <div className={styles.card}>
        <div className={styles.cardBody}>
          <p className={styles.groupLabel}>
            {AppStrings.POKER_CASH_GAME_TABLE_SIZE}
          </p>
          <div className={styles.row}>
            <Form.Item
              className={styles.field}
              name="maxPlayers"
              label={AppStrings.POKER_CASH_GAME_MAX_SEATS}
              rules={[requiredRule, numberRule({ integer: true })]}
            >
              <StepperInput
                inputMode="numeric"
                step={1}
                min={2}
                data-testid="cash-game-max-players"
                aria-label={AppStrings.POKER_CASH_GAME_MAX_SEATS}
              />
            </Form.Item>
            <Form.Item
              className={styles.field}
              name="minPlayers"
              label={AppStrings.POKER_CASH_GAME_MIN_PLAYERS}
              dependencies={["maxPlayers"]}
              rules={[
                requiredRule,
                numberRule({ integer: true }),
                minPlayersBelowMax,
              ]}
            >
              <StepperInput
                inputMode="numeric"
                step={1}
                min={2}
                data-testid="cash-game-min-players"
                aria-label={AppStrings.POKER_CASH_GAME_MIN_PLAYERS}
              />
            </Form.Item>
          </div>

          <p className={styles.groupLabel}>
            {AppStrings.POKER_CASH_GAME_STAKES}
          </p>
          <div className={styles.row}>
            <Form.Item
              className={styles.field}
              name="smallBlind"
              label={AppStrings.POKER_CASH_GAME_SMALL_BLIND}
              rules={[
                requiredRule,
                numberRule({ integer: false }),
                twoDecimalsRule,
              ]}
            >
              <StepperInput
                ladder={BLIND_LADDER}
                data-testid="cash-game-small-blind"
                aria-label={AppStrings.POKER_CASH_GAME_SMALL_BLIND}
              />
            </Form.Item>
            <Form.Item
              className={styles.field}
              name="bigBlind"
              label={AppStrings.POKER_CASH_GAME_BIG_BLIND}
              dependencies={["smallBlind"]}
              rules={[
                requiredRule,
                numberRule({ integer: false }),
                twoDecimalsRule,
                bigBlindRatioRule,
              ]}
            >
              <StepperInput
                ladder={BLIND_LADDER}
                data-testid="cash-game-big-blind"
                aria-label={AppStrings.POKER_CASH_GAME_BIG_BLIND}
              />
            </Form.Item>
          </div>

          <p className={styles.groupLabel}>
            {AppStrings.POKER_CASH_GAME_BUY_IN}
          </p>
          <div className={styles.row}>
            <Form.Item
              className={styles.field}
              name="buyInBbMin"
              label={AppStrings.POKER_CASH_GAME_MIN_BUY_IN_MULTIPLIER}
              dependencies={["buyInBbMax"]}
              rules={[
                requiredRule,
                numberRule({ integer: true }),
                minBuyInFloorRule,
                minBuyInBelowMax,
              ]}
            >
              <StepperInput
                inputMode="numeric"
                step={5}
                min={BUY_IN_BB_FLOOR}
                hint={AppStrings.POKER_CASH_GAME_MIN_BUY_IN_HINT}
                data-testid="cash-game-min-buy-in"
                aria-label={AppStrings.POKER_CASH_GAME_MIN_BUY_IN_MULTIPLIER}
              />
            </Form.Item>
            <Form.Item
              className={styles.field}
              name="buyInBbMax"
              label={AppStrings.POKER_CASH_GAME_MAX_BUY_IN_MULTIPLIER}
              rules={[
                requiredRule,
                numberRule({ integer: true }),
                maxBuyInCeilingRule,
              ]}
            >
              <StepperInput
                inputMode="numeric"
                step={5}
                min={BUY_IN_BB_FLOOR}
                hint={AppStrings.POKER_CASH_GAME_MAX_BUY_IN_HINT}
                data-testid="cash-game-max-buy-in"
                aria-label={AppStrings.POKER_CASH_GAME_MAX_BUY_IN_MULTIPLIER}
              />
            </Form.Item>
          </div>

          <p className={styles.groupLabel}>
            {AppStrings.POKER_CASH_GAME_TABLE_PACING}
          </p>
          <div className={styles.row}>
            <Form.Item
              className={styles.field}
              name="gameStartDelay"
              label={AppStrings.POKER_CASH_GAME_GAME_START_DELAY}
              rules={[requiredRule, numberRule({ integer: true, min: "zero" })]}
            >
              <StepperInput
                inputMode="numeric"
                step={1}
                min={0}
                hint={AppStrings.POKER_CASH_GAME_SECONDS_HINT}
                data-testid="cash-game-game-start-delay"
                aria-label={AppStrings.POKER_CASH_GAME_GAME_START_DELAY}
              />
            </Form.Item>
            <Form.Item
              className={styles.field}
              name="handStartDelay"
              label={AppStrings.POKER_CASH_GAME_HAND_START_DELAY}
              rules={[requiredRule, numberRule({ integer: true, min: "zero" })]}
            >
              <StepperInput
                inputMode="numeric"
                step={1}
                min={0}
                hint={AppStrings.POKER_CASH_GAME_SECONDS_HINT}
                data-testid="cash-game-hand-start-delay"
                aria-label={AppStrings.POKER_CASH_GAME_HAND_START_DELAY}
              />
            </Form.Item>
          </div>

          <p className={styles.groupLabel}>{AppStrings.POKER_CASH_GAME_RAKE}</p>
          <div className={styles.row}>
            <Form.Item
              className={styles.field}
              name="rakePercentage"
              label={AppStrings.POKER_CASH_GAME_RAKE_PERCENTAGE}
              rules={[
                requiredRule,
                numberRule({
                  integer: false,
                  min: "zero",
                  max: {
                    value: 100,
                    message: AppStrings.POKER_CASH_GAME_PERCENT_RANGE,
                  },
                }),
              ]}
            >
              <StepperInput
                step={0.5}
                min={0}
                hint={AppStrings.POKER_CASH_GAME_RAKE_PERCENT_HINT}
                data-testid="cash-game-rake-percentage"
                aria-label={AppStrings.POKER_CASH_GAME_RAKE_PERCENTAGE}
              />
            </Form.Item>
            <Form.Item
              className={styles.field}
              name="rakeCap"
              label={AppStrings.POKER_CASH_GAME_RAKE_CAP}
              rules={[
                requiredRule,
                numberRule({ integer: false, min: "zero" }),
                twoDecimalsRule,
              ]}
            >
              <StepperInput
                step={0.5}
                min={0}
                data-testid="cash-game-rake-cap"
                aria-label={AppStrings.POKER_CASH_GAME_RAKE_CAP}
              />
            </Form.Item>
          </div>
          <ToggleField
            name="noFlopNoDrop"
            label={AppStrings.POKER_CASH_GAME_NO_FLOP_NO_DROP}
            hint={AppStrings.POKER_CASH_GAME_NO_FLOP_NO_DROP_HINT}
            testId="cash-game-no-flop-no-drop"
          />
        </div>

        {/* Footer lives inside the card, wizard-style (border-top divider). */}
        <div className={styles.footer}>
          {isDirty && (
            <span
              className={styles.unsavedChip}
              data-testid="cash-game-unsaved-chip"
            >
              {AppStrings.POKER_CASH_GAME_UNSAVED_CHANGES}
            </span>
          )}
          <Button
            intent="neutral"
            variant="outline"
            onClick={handleDiscard}
            disabled={!isDirty || updateMutation.isPending}
            data-testid="cash-game-discard"
          >
            {AppStrings.POKER_CASH_GAME_DISCARD}
          </Button>
          <Button
            intent="primary"
            onClick={handleSave}
            disabled={saveDisabled}
            loading={updateMutation.isPending}
            data-testid="cash-game-save"
          >
            {AppStrings.SAVE}
          </Button>
        </div>
      </div>
    </Form>
  );
}

/**
 * Poker → Configuration → Cash Game Limits (POKENG-141/191). The backend
 * stores four independent configs keyed by currency × visibility, so the
 * page starts with a selector for which record is being edited; the form
 * below covers table size, stakes, buy-in, pacing, rake, and player
 * experience, and saves the full payload back per config.
 */
function CashGameLimits() {
  const [configKey, setConfigKey] = useState<CashGameConfigKey>({
    currency: "cash",
    visibility: "public",
  });
  const [isDirty, setIsDirty] = useState(false);
  // POKENG-191: whole page ships behind a flag, off by default (AGENTS.md
  // §3). Off / still-resolving shows the pre-existing "Coming soon"
  // placeholder and never issues the v2 config call.
  const { isEnabled: isCashGameConfigEnabled, loading: isFlagLoading } =
    usePamFeature("pokerCashGameConfigV2");
  const flagOn = isCashGameConfigEnabled === true && !isFlagLoading;
  const { data, isLoading, isError, refetch } = useCashGameConfig(configKey, {
    enabled: flagOn,
  });

  if (!flagOn) {
    return <PokerConfigurationPlaceholder page={PAGE} />;
  }

  const changeKey = (next: CashGameConfigKey) => {
    // Silently dropping unsaved edits on switch is a data-loss footgun —
    // force an explicit Save or Discard first.
    if (isDirty) {
      message.warning(AppStrings.POKER_CASH_GAME_SWITCH_BLOCKED);
      return;
    }
    setConfigKey(next);
  };

  // Environment context so limits aren't edited against the wrong backend
  // (PAM_APP_NAME is env-specific, e.g. "test03" vs the client brand name).
  const envBadge = (
    <span className={styles.envBadge} data-testid="cash-game-env-badge">
      {`${AppStrings.POKER_CASH_GAME_ENVIRONMENT}: ${AppStrings.PAM_APP_NAME}`}
    </span>
  );

  return (
    <PokerConfigurationShell page={PAGE} headerExtra={envBadge}>
      <div className={styles.selectorRow}>
        <div className={styles.selectorGroup}>
          <span className={styles.selectorLabel}>
            {AppStrings.POKER_CASH_GAME_CURRENCY}
          </span>
          <SegmentedControl<CashGameCurrency>
            items={CURRENCY_ITEMS}
            value={configKey.currency}
            onChange={(currency) => changeKey({ ...configKey, currency })}
            ariaLabel={AppStrings.POKER_CASH_GAME_CURRENCY}
            data-testid="cash-game-currency"
          />
        </div>
        <div className={styles.selectorGroup}>
          <span className={styles.selectorLabel}>
            {AppStrings.POKER_CASH_GAME_VISIBILITY}
          </span>
          <SegmentedControl<CashGameVisibility>
            items={VISIBILITY_ITEMS}
            value={configKey.visibility}
            onChange={(visibility) => changeKey({ ...configKey, visibility })}
            ariaLabel={AppStrings.POKER_CASH_GAME_VISIBILITY}
            data-testid="cash-game-visibility"
          />
        </div>
        {data?.version !== null && data?.version !== undefined && (
          <span className={styles.auditLine} data-testid="cash-game-audit">
            {`${AppStrings.POKER_CASH_GAME_VERSION} ${data.version}`}
            {data.updatedBy
              ? ` · ${AppStrings.POKER_CASH_GAME_LAST_UPDATED}: ${data.updatedBy}`
              : ""}
            {data.updatedAt
              ? ` (${formatDate(data.updatedAt, "D MMM YYYY, HH:mm")})`
              : ""}
          </span>
        )}
      </div>
      {isLoading ? (
        <div className={styles.stateBox} data-testid="cash-game-loading">
          <RebetLoader height="64px" width="64px" />
        </div>
      ) : (
        <>
          {isError && (
            // Deliberately not the raw API message: an unreachable backend
            // surfaces gateway/auth internals that must not hit the UI. The
            // form still renders (blank) so the page is reviewable before
            // the config API (POKENG-142) ships.
            <div
              className={styles.loadErrorBanner}
              role="alert"
              data-testid="cash-game-load-error"
            >
              <div>
                <p className={styles.loadErrorTitle}>
                  {AppStrings.POKER_CASH_GAME_LOAD_ERROR}
                </p>
                <p className={styles.loadErrorHint}>
                  {AppStrings.POKER_CASH_GAME_LOAD_ERROR_HINT}
                </p>
              </div>
              <Button
                intent="neutral"
                variant="outline"
                onClick={() => refetch()}
                data-testid="cash-game-retry"
              >
                {AppStrings.POKER_CASH_GAME_TRY_AGAIN}
              </Button>
            </div>
          )}
          {/* Remount when the selected config or its data changes so the
              loaded values replace the previous baseline. Before a config
              exists, every field prefills with the poker-standard
              defaults rather than blanks. */}
          <CashGameLimitsForm
            key={`${configKey.currency}-${configKey.visibility}-${
              data ? "loaded" : "blank"
            }`}
            configKey={configKey}
            initialConfig={data?.config ?? CONFIG_DEFAULTS}
            onDirtyChange={setIsDirty}
          />
        </>
      )}
    </PokerConfigurationShell>
  );
}

export default CashGameLimits;
